import { useCallback } from 'react';
import { DateRange } from '@/shared/types/datepicker';

// Función auxiliar para validar fechas
const isValidDate = (date: unknown): date is Date => {
  return date !== null && date !== undefined && date instanceof Date && !isNaN(date.getTime());
};

export const useDateRange = (
  selectedRange: DateRange,
  onRangeChange: (range: DateRange) => void,
  minDate?: Date,
  maxDate?: Date,
  autoCloseRange?: boolean
) => {
  const isDateInRange = useCallback((date: Date, range: DateRange) => {
    if (!range.start || !range.end || !isValidDate(range.start) || !isValidDate(range.end))
      return false;
    return date >= range.start && date <= range.end;
  }, []);

  const isDateRangeStart = useCallback((date: Date, range: DateRange) => {
    if (!range.start || !isValidDate(range.start)) return false;
    return date.getTime() === range.start.getTime();
  }, []);

  const isDateRangeEnd = useCallback((date: Date, range: DateRange) => {
    if (!range.end || !isValidDate(range.end)) return false;
    return date.getTime() === range.end.getTime();
  }, []);

  const isDateDisabled = useCallback(
    (date: Date) => {
      if (!isValidDate(date)) return true;
      if (minDate && isValidDate(minDate) && date < minDate) return true;
      if (maxDate && isValidDate(maxDate) && date > maxDate) return true;
      return false;
    },
    [minDate, maxDate]
  );

  const handleRangeSelection = useCallback(
    (date: Date, hoverDate: Date | null) => {
      // Validar que date sea una fecha válida
      if (!isValidDate(date)) {
        if (process.env.NODE_ENV !== 'production') {
          // eslint-disable-next-line no-console
          console.warn('useDateRange: Se recibió una fecha inválida:', date);
        }
        return;
      }

      if (isDateDisabled(date)) return;

      const newRange: DateRange = { ...selectedRange };

      if (!selectedRange.start || (selectedRange.start && selectedRange.end)) {
        // Iniciar nueva selección
        newRange.start = date;
        newRange.end = null;
      } else {
        // Completar la selección
        if (date < selectedRange.start) {
          newRange.start = date;
          newRange.end = selectedRange.start;
        } else {
          newRange.start = selectedRange.start;
          newRange.end = date;
        }
      }

      onRangeChange(newRange);

      // El cierre automático se maneja en useDatePicker para evitar duplicación
      // No cerramos aquí para mantener la lógica centralizada
    },
    [selectedRange, onRangeChange, isDateDisabled]
  );

  const getRangeState = useCallback(
    (date: Date, hoverDate: Date | null) => {
      const isStart = isDateRangeStart(date, selectedRange);
      const isEnd = isDateRangeEnd(date, selectedRange);
      const isInRange = isDateInRange(date, selectedRange);
      const isDisabled = isDateDisabled(date);

      // Calcular estado de hover para rangos
      let isHovered = false;
      if (hoverDate && selectedRange.start && !selectedRange.end) {
        const tempRange: DateRange = {
          start: selectedRange.start < hoverDate ? selectedRange.start : hoverDate,
          end: selectedRange.start < hoverDate ? hoverDate : selectedRange.start,
        };
        isHovered = isDateInRange(date, tempRange);
      }

      // Calcular si es el primer día del rango
      let isFirstInRange = false;

      // Si hay un rango completado
      if (selectedRange.start && selectedRange.end && isInRange && !isStart) {
        // Verificar si el día anterior está fuera del rango
        const prevDay = new Date(date);
        prevDay.setDate(date.getDate() - 1);
        const isPrevDayInRange = isDateInRange(prevDay, selectedRange);

        // Si el día anterior no está en el rango, entonces este es el primero
        isFirstInRange = !isPrevDayInRange;
      }
      // Si hay un rango en progreso (solo start, sin end) y hay hover
      else if (selectedRange.start && !selectedRange.end && hoverDate && isHovered) {
        // Verificar si el día anterior también está en hover
        const prevDay = new Date(date);
        prevDay.setDate(date.getDate() - 1);

        // Crear rango temporal para el hover
        const tempRange: DateRange = {
          start: selectedRange.start < hoverDate ? selectedRange.start : hoverDate,
          end: selectedRange.start < hoverDate ? hoverDate : selectedRange.start,
        };

        const isPrevDayInTempRange = isDateInRange(prevDay, tempRange);

        // Si el día anterior no está en el rango temporal, entonces este es el primero
        isFirstInRange = !isPrevDayInTempRange;
      }

      // Calcular si es el último día del rango
      let isLastInRange = false;

      // Si hay un rango completado
      if (selectedRange.start && selectedRange.end && isInRange && !isEnd) {
        // Verificar si el siguiente día está fuera del rango
        const nextDay = new Date(date);
        nextDay.setDate(date.getDate() + 1);
        const isNextDayInRange = isDateInRange(nextDay, selectedRange);

        // Si el siguiente día no está en el rango, entonces este es el último
        isLastInRange = !isNextDayInRange;
      }
      // Si hay un rango en progreso (solo start, sin end) y hay hover
      else if (selectedRange.start && !selectedRange.end && hoverDate && isHovered) {
        // Verificar si el siguiente día también está en hover
        const nextDay = new Date(date);
        nextDay.setDate(date.getDate() + 1);

        // Crear rango temporal para el hover
        const tempRange: DateRange = {
          start: selectedRange.start < hoverDate ? selectedRange.start : hoverDate,
          end: selectedRange.start < hoverDate ? hoverDate : selectedRange.start,
        };

        const isNextDayInTempRange = isDateInRange(nextDay, tempRange);

        // Si el siguiente día no está en el rango temporal, entonces este es el último
        isLastInRange = !isNextDayInTempRange;
      }

      // Calcular si el hover está por delante de la fecha seleccionada
      let afterSelected = true;
      if (selectedRange.start && !selectedRange.end && hoverDate) {
        // Si el hover está en o después de la fecha inicial, afterSelected = true
        afterSelected = hoverDate >= selectedRange.start;
      } else if (selectedRange.start && selectedRange.end) {
        // Si hay un rango completado, afterSelected = true si la fecha actual está en o después del start
        afterSelected = date >= selectedRange.start;
      }

      return {
        isSelectingStart: !selectedRange.start || (selectedRange.start && selectedRange.end),
        isSelectingEnd: !!selectedRange.start && !selectedRange.end,
        isStart,
        isEnd,
        isInRange,
        isFirstInRange,
        isLastInRange,
        afterSelected,
        isDisabled,
        isHovered,
      };
    },
    [selectedRange, isDateRangeStart, isDateRangeEnd, isDateInRange, isDateDisabled]
  );

  return {
    isDateInRange,
    isDateRangeStart,
    isDateRangeEnd,
    isDateDisabled,
    handleRangeSelection,
    getRangeState,
  };
};
